import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react'; // Assuming Clerk is used for user context
import { useNavigate } from 'react-router-dom'; // Assuming React Router for navigation
import axios from 'axios';
// Import Lucide Icons
import {
    Loader2, // For loading state
    Eye, // View Agent
    BarChart3, // Stats
    Trash2, // Kill Agent / Delete
    X, // Close Modal
    PlusCircle, // Create Agent
    ShieldAlert,
    Bot// Warning / Kill Action emphasis
} from 'lucide-react';

// --- Animation Utility Hook ( Reuse from EFoundry ) ---
const useOnScreen = (options = { threshold: 0.1 }) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') { setIsVisible(true); return; }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(entry.target); }
        }, options);
        const currentRef = ref.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef && observer) observer.unobserve(currentRef); };
    }, [options]);
    const hookRef = (node) => { ref.current = node; };
    return [hookRef, isVisible];
};

// --- Styled Clickable Div Component --- (Helper for replacing buttons)
const ClickableDiv = ({ children, className, onClick, disabled, ...props }) => (
    <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={!disabled ? onClick : undefined}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e); }}
        className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${className}`}
        aria-disabled={disabled}
        {...props}
    >
        {children}
    </div>
);

// --- MyAgents Component ---
const MyAgents = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showKillModal, setShowKillModal] = useState(false);
  const cid = user?.id;

  // Animation Hook
  const [listRef, isListVisible] = useOnScreen({ threshold: 0.05 });

  useEffect(() => {
    if (cid) {
      fetchAgents();
    } else if (!user && user !== undefined) {
        setLoading(false);
        setError("User not authenticated.");
    }
  }, [cid]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      if (!cid) { throw new Error("User ID not available."); }
      const response = await axios.post('https://web-production-5ee8.up.railway.app/echidna/myagents', { cid });
      setAgents(response.data.agents || []);
      setError(null);
    } catch (err) {
      console.error("Fetch Agents Error:", err);
      setError(err.message || err.response?.data?.details || 'Failed to fetch agents');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAgent = (agentId) => { navigate(`/echidna/agent/${agentId}`); };
  const handleViewStats = (agentId) => { navigate(`/echidna/stats/${agentId}`); };
  const openKillModal = (agent) => { setSelectedAgent(agent); setShowKillModal(true); setConfirmText(''); setSecretKey(''); setError(null); };

  const handleKillAgent = async () => {
    if (!selectedAgent || confirmText.toLowerCase() !== 'i confirm' || !secretKey) {
        setError('Please type "i confirm" and enter the secret key.');
      return;
    }
    try {
        setError(null);
      const response = await axios.post('https://web-production-5ee8.up.railway.app/echidna/killagent', { secretKey, agentId: selectedAgent._id, cid });
      setShowKillModal(false);
      fetchAgents();
    } catch (err) {
      console.error("Kill Agent Error:", err);
      setError(err.response?.data?.message || 'Failed to delete agent. Check secret key or agent status.');
    }
  };

  // Loading State
  if (loading && !agents.length && !error) {
    return (
      // Container takes full screen height, content centered
      <div className="h-screen bg-black flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    // Outer container is exactly screen height
    <div className="h-screen bg-black text-white">
       {/* Inner container handles padding and scrolling */}
       <div className="p-6 lg:p-8 h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-950">
          <header className="mb-10">
            <h1 className="text-3xl font-bold tracking-tight text-gray-100 mb-2">My Assistants</h1>
            <p className="text-base text-gray-400">Manage your created AI assistants and tools.</p>
          </header>

          {error && !showKillModal && (
            <div className="mb-8 p-4 border border-red-700/50 bg-gray-950 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {!loading && agents.length === 0 && !error ? (
            <div ref={listRef} className={`flex flex-col items-center justify-center text-center bg-gray-950 border border-dashed border-gray-800/50 rounded-lg py-16 px-6 transition-opacity duration-500 ${isListVisible ? 'opacity-100' : 'opacity-0'}`}>
              <Bot className="w-12 h-12 text-gray-700 mb-4" strokeWidth={1}/>
              <p className="text-gray-500 mb-6">You haven't created any assistants yet.</p>
              <a
                href="/echidna/new-agent"
                onClick={(e) => { e.preventDefault(); navigate('/echidna/new-agent'); }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg shadow-lg shadow-purple-600/30 hover:bg-purple-700 transition-colors"
              >
                <PlusCircle className="w-5 h-5" strokeWidth={1.5} />
                Create an Assistant
              </a>
            </div>
          ) : (
            <div ref={listRef} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {agents.map((agent, index) => (
                <div
                  key={agent._id}
                  className={`bg-gray-950 rounded-lg border border-gray-800/50 p-5 flex flex-col transition-all duration-500 ease-out hover:border-purple-600/50 hover:shadow-xl hover:-translate-y-1 ${isListVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 75}ms` }}
                >
                  <h2 className="text-lg font-semibold text-gray-100 mb-2 truncate">{agent.agentName}</h2>
                  <div className="text-gray-500 text-xs mb-5 space-y-0.5">
                    <p>Created: {new Date(agent.createdAt).toLocaleDateString()}</p>
                    <p className="truncate font-mono">ID: {agent._id}</p>
                  </div>
                  <div className="mt-auto grid grid-cols-3 gap-2 pt-4 border-t border-gray-800/50">
                    <ClickableDiv onClick={() => handleViewAgent(agent._id)} className="text-xs font-medium text-gray-300 border border-gray-700/80 py-1.5 justify-center hover:bg-gray-800/50 hover:border-gray-700 hover:text-white group" title="View Details">
                       <Eye className="w-3.5 h-3.5 mr-1 group-hover:text-purple-400" strokeWidth={1.5} /> View
                    </ClickableDiv>
                    <ClickableDiv onClick={() => handleViewStats(agent._id)} className="text-xs font-medium text-gray-300 border border-gray-700/80 py-1.5 justify-center hover:bg-gray-800/50 hover:border-gray-700 hover:text-white group" title="View Stats">
                       <BarChart3 className="w-3.5 h-3.5 mr-1 group-hover:text-purple-400" strokeWidth={1.5} /> Stats
                    </ClickableDiv>
                     <ClickableDiv onClick={() => openKillModal(agent)} className="text-xs font-medium text-red-500/80 border border-red-900/50 bg-red-900/10 py-1.5 justify-center hover:bg-red-900/30 hover:border-red-700 hover:text-red-400 group" title="Delete Agent">
                       <Trash2 className="w-3.5 h-3.5 mr-1 group-hover:animate-bounce-quick" strokeWidth={1.5} /> Kill
                    </ClickableDiv>
                  </div>
                </div>
              ))}
            </div>
          )}

         {/* Inner Padding Div ends */}
         </div>

          {/* Kill Agent Modal */}
          {showKillModal && (
            <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast">
              <div className="bg-gray-950 border border-gray-800/50 rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-red-400 flex items-center">
                        <ShieldAlert className="w-5 h-5 mr-2" strokeWidth={1.5}/> Kill Assistant
                    </h2>
                    <div role="button" tabIndex={0} onClick={() => setShowKillModal(false)} className="p-1 -m-1 rounded-full text-gray-500 hover:bg-gray-800/50 hover:text-white cursor-pointer">
                         <X className="w-5 h-5" strokeWidth={1.5}/>
                     </div>
                </div>

                <p className="text-sm text-gray-400 mb-4">Permanently delete <strong className="text-gray-200">{selectedAgent?.agentName}</strong>?</p>

                <div className="mb-6 p-3 bg-red-900/20 border border-red-700/30 rounded-md">
                  <p className="text-xs text-red-400">Warning: This action cannot be undone. All associated data, configurations, and interactions will be permanently lost.</p>
                </div>

                {error && showKillModal && (
                     <p className="text-xs text-red-400 mb-4">{error}</p>
                )}

                <div className="mb-4">
                  <label htmlFor="confirm-text" className="block text-xs text-gray-400 mb-1.5">To confirm, type: <code className="text-purple-400 bg-gray-800 px-1 py-0.5 rounded text-xs">i confirm</code></label>
                  <input
                    id="confirm-text" type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                    className="w-full bg-black/50 border border-gray-700 p-2 text-sm text-white rounded-md focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600"
                    placeholder="i confirm"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="secret-key" className="block text-xs text-gray-400 mb-1.5">Enter Agent Secret Key</label>
                  <input
                    id="secret-key" type="password" value={secretKey} onChange={(e) => setSecretKey(e.target.value)}
                    className="w-full bg-black/50 border border-gray-700 p-2 text-sm text-white rounded-md focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600"
                    placeholder="********"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                   <ClickableDiv onClick={() => setShowKillModal(false)} className="px-4 py-1.5 text-sm border border-gray-700 hover:bg-gray-800 transition-colors text-gray-300" >
                       Cancel
                   </ClickableDiv>
                   <ClickableDiv
                        onClick={handleKillAgent}
                        disabled={confirmText.toLowerCase() !== 'i confirm' || !secretKey}
                        className={`px-4 py-1.5 text-sm border ${ confirmText.toLowerCase() === 'i confirm' && secretKey ? 'border-red-700/50 bg-red-800/80 hover:bg-red-700/80 text-white' : 'border-gray-700 bg-gray-800 text-gray-500'}`} // Disabled styles handled by helper
                   >
                        <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.5}/> Delete Permanently
                   </ClickableDiv>
                </div>
              </div>
            </div>
          )}
          {/* Global styles needed for animations / scrollbars */}
          <style jsx global>{`
             @keyframes bounce-quick { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
             .animate-bounce-quick { animation: bounce-quick 0.3s ease-in-out; }
             @keyframes fade-in-fast { from { opacity: 0; } to { opacity: 1; } }
             .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }
             .scrollbar-thin { scrollbar-width: thin; scrollbar-color: theme(colors.gray.700) transparent; }
             .scrollbar-thin::-webkit-scrollbar { width: 5px; height: 5px; }
             .scrollbar-thumb-gray-700::-webkit-scrollbar-thumb { background-color: theme(colors.gray.700); border-radius: 3px; }
             .scrollbar-track-gray-950::-webkit-scrollbar-track { background-color: transparent; }
          `}</style>
    </div>
  );
};

export default MyAgents;

// Note: Remember that this MyAgents component is designed to be rendered *inside* the MainContent area
// of the EFoundry layout component (which provides the Sidebar and TopBar).
// You would replace the previous content inside MainContent's <main> tag with <MyAgents />.